import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../utils/formatters';
import { Paper } from '@mui/material';
import { LocalHospital } from '@mui/icons-material';
import dayjs from 'dayjs';
import DmsDatePicker from "../../components/DmsDatePicker/DmsDatePicker";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    DmsAltBox,
    DmsButton,
    DmsPage,
    DmsSelect,
    DmsTextField,
    DmsAlertWarning,
    DmsModal,
    fetchData,
    DmsCheckBox,
    intl,
    HTTP_STATUS,
} from 'dmsbasereact';
import { DmsAutocomplete } from '../../components/DmsAutoComplete/DmsAutoComplete';
import { DmsElectronicSignature } from '../../components/DmsElectronicSignature';
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Link,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LegalDocumentDialog } from '../../components/LegalDocumentDialog';
import Swal from 'sweetalert2';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { StrengthUnit } from '../../constants/StrenghtUnit';
import { DispensingStatusEnum } from '../../constants/DispensingStatus';
import { PatientStatusEnum } from '../../constants/PatientStatusEnum';
import { InstitutionStatusEnum, InstitutionStatus } from '../../constants/InstitutionStatus';
import { isExpired } from '../../utils/expiration';
import { PRESCRIPTION_STATUS } from '../../constants/PrescriptionStatus';
import { useInstitutionStatus } from '../../hooks/useInstitutionStatus';
import { openPrescriptionPdfForPrint } from '../../utils/downloadPrescriptionPdf';
import { openDispensingReceiptForPrint } from '../../utils/downloadDispensingReceipt';

// Returned by a function so dispensation_date is evaluated NOW each time the
// form resets — not frozen to whatever "today" was when the JS bundle loaded.
const buildDefaultFormState = () => ({
    organization_branch_pk: '',
    ppg_counseling_confirmed: false,
    liability_term_received: false,
    dosage_rectification_term_accepted: false,
    allow_packaging_adjustment: false,
    dispensation_date: dayjs(), // Default to today
});

const MIN_SEARCH_LENGTH = 3;

const toInputValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value);
};

/**
 * Extracts capsule count from part name using regex.
 * Example: "NUVYOR 10MG 14CAP (C3)" → 14
 * @param {string} partName - The part name
 * @returns {number} - Extracted count or 0 if not found
 */
const extractCapsuleCountFromName = (partName) => {
    if (!partName) return 0;
    const match = partName.match(/(\d+)\s*CAP/i);
    return match ? parseInt(match[1], 10) : 0;
};

export const DispensationPage = () => {
    const [dependencies, setDependencies] = useState({
        pharmacists: [],
        organization_branches: [],
        batches: [],
        dispensing_statuses: [],
    });
    const [prescriptions, setPrescriptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadingPrescriptions, setLoadingPrescriptions] = useState(false);
    const [loadingDependencies, setLoadingDependencies] = useState(false);
    const [packagingMaxDiff, setPackagingMaxDiff] = useState({});
    const [packagingAvailableSizes, setPackagingAvailableSizes] = useState({});
    const [selectedPrescription, setSelectedPrescription] = useState(null);
    const [formState, setFormState] = useState(buildDefaultFormState);
    const [batchSelections, setBatchSelections] = useState([
        { batch_pk: null, box_count: 1, capsule_count: '', capsule_count_readonly: false, batch_details: null, batch_not_listed: false, global_options: [], loading_global: false }
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [showTrainingWarningModal, setShowTrainingWarningModal] = useState(false);
    const [trainingWarningMessage, setTrainingWarningMessage] = useState('');
    const [trainingWarningTitle, setTrainingWarningTitle] = useState('');
    const [showDoubleBlindModal, setShowDoubleBlindModal] = useState(false);
    const [showJustificationModal, setShowJustificationModal] = useState(false);
    const [justification, setJustification] = useState('');
    const [termDialogOpen, setTermDialogOpen] = useState(false);
    const [termDocument, setTermDocument] = useState(null);
    const [loadingTerm, setLoadingTerm] = useState(false);
    const [accountStatus, setAccountStatus] = useState(null);
    const [selectedInstitutionInactive, setSelectedInstitutionInactive] = useState(false);
    const [institutionApprovalStatus, setInstitutionApprovalStatus] = useState(null);
    const [selectedInstitutionNotApproved, setSelectedInstitutionNotApproved] = useState(false);
    const [selectedInstitutionOwnStatusLabel, setSelectedInstitutionOwnStatusLabel] = useState('');

    // Get user's institution approval statuses
    const { institutionStatuses, isApprovedAtInstitution, getInstitutionStatus } = useInstitutionStatus();

    const loadingLabel = intl.get('Loading').d('Loading...');

    const resetForm = useCallback((deps = dependencies) => {
        const baseState = {
            ...buildDefaultFormState(),
            organization_branch_pk: toInputValue(deps?.organization_branches?.[0]?.pk),
        };

        setFormState(baseState);

        // Reset batch selections to single empty row
        setBatchSelections([
            { batch_pk: null, box_count: 1, batch_details: null, batch_not_listed: false, global_options: [], loading_global: false }
        ]);
    }, [dependencies]);

    // Batch management functions
    const addBatchSelection = useCallback(() => {
        // Find next available batch (FEFO order, exclude already selected)
        const selectedPks = batchSelections.map(s => s.batch_pk);
        const nextBatch = dependencies.batches.find(b => !selectedPks.includes(b.pk));

        // Auto-extract capsule count from batch part_name
        const extractedCapsules = nextBatch ? extractCapsuleCountFromName(nextBatch.part_name) : 0;

        setBatchSelections(prev => [
            ...prev,
            {
                batch_pk: nextBatch?.pk || null,
                box_count: 1,
                capsule_count: extractedCapsules > 0 ? extractedCapsules.toString() : '',
                capsule_count_readonly: extractedCapsules > 0,
                batch_details: nextBatch || null,
                batch_not_listed: false,
                global_options: [],
                loading_global: false
            }
        ]);
    }, [batchSelections, dependencies.batches]);

    const removeBatchSelection = useCallback((index) => {
        if (batchSelections.length > 1) {
            setBatchSelections(prev => prev.filter((_, i) => i !== index));
        }
    }, [batchSelections.length]);

    const handleBatchChange = useCallback((index, batchPk) => {
        setBatchSelections(prev => {
            const selection = prev[index];
            const source = selection.batch_not_listed ? selection.global_options : dependencies.batches;
            const batch = source.find(b => b.pk === batchPk);
            const extractedCapsules = batch ? extractCapsuleCountFromName(batch.part_name) : 0;
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                batch_pk: batchPk,
                batch_details: batch,
                capsule_count: extractedCapsules > 0 ? extractedCapsules.toString() : updated[index].capsule_count,
                capsule_count_readonly: extractedCapsules > 0
            };
            return updated;
        });
    }, [dependencies.batches]);

    const handleBatchNotListedChange = useCallback((index, checked) => {
        setBatchSelections(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                batch_not_listed: checked,
                batch_pk: null,
                batch_details: null,
                capsule_count: '',
                capsule_count_readonly: false,
                global_options: [],
                loading_global: false,
            };
            return updated;
        });
    }, []);

    const handleBatchBoxCountChange = useCallback((index, boxCount) => {
        setBatchSelections(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                box_count: parseInt(boxCount) || 1
            };
            return updated;
        });
    }, []);

    const handleBatchCapsuleCountChange = useCallback((index, value) => {
        // Allow empty string or positive integers only
        if (value === '' || /^\d+$/.test(value)) {
            setBatchSelections(prev => {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    capsule_count: value
                };
                return updated;
            });
        }
    }, []);

    const handleDateChange = useCallback((newDate) => {
        setFormState((prev) => ({
            ...prev,
            dispensation_date: newDate
        }));
    }, []);

    // Calculation helpers
    const calculateTotalBoxes = useCallback(() => {
        return batchSelections.reduce((sum, s) => sum + (s.box_count || 0), 0);
    }, [batchSelections]);

    const calculateTotalCapsules = useCallback(() => {
        return batchSelections.reduce((sum, s) => {
            const capsuleCount = Number(s.capsule_count) || 0;
            return sum + (s.box_count || 0) * capsuleCount;
        }, 0);
    }, [batchSelections]);

    const extractStrengthFromName = (partName) => {
        if (!partName) return 0;
        const match = partName.match(/(\d+)\s*mg/i);
        return match ? parseInt(match[1]) : 0;
    };

    const calculateBatchMg = useCallback((selection) => {
        if (!selection.batch_details) return 0;
        const strength = extractStrengthFromName(selection.batch_details.part_name);
        const capsuleCount = Number(selection.capsule_count) || 0;
        return selection.box_count * capsuleCount * strength;
    }, []);

    const calculateTotalMg = useCallback(() => {
        return batchSelections.reduce((sum, s) => sum + calculateBatchMg(s), 0);
    }, [batchSelections, calculateBatchMg]);

    const loadDependencies = useCallback(async () => {
        setLoadingDependencies(true);
        try {
            // Fetch packaging rules max diff in parallel
            fetchData('/dispensing/packaging-rules/max-diff', 'GET', true)
                .then(res => {
                    if (res.status === HTTP_STATUS.OK_200) {
                        setPackagingMaxDiff(res?.data?.data || {});
                        setPackagingAvailableSizes(res?.data?.available_sizes || {});
                    }
                })
                .catch(() => {});

            const response = await fetchData('/dispensing/dependencies', 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                const payload = response?.data?.data ?? response?.data ?? {};
                setDependencies(payload);

                // Fetch batches for the first institution if available
                const firstInstitution = payload?.organization_branches?.[0]?.pk;
                if (firstInstitution) {
                    setLoadingBatches(true);
                    try {
                        const batchResponse = await fetchData(
                            `/dispensing/batches-by-institution?organization_branch_pk=${firstInstitution}`,
                            'GET',
                            true
                        );
                        if (batchResponse.status === HTTP_STATUS.OK_200) {
                            const batches = batchResponse?.data?.data ?? [];
                            setDependencies(prev => ({
                                ...prev,
                                batches: batches,
                            }));
                        }
                    } catch (error) {
                        console.error('Error loading initial batches:', error);
                    } finally {
                        setLoadingBatches(false);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading dependencies:', error);
        } finally {
            setLoadingDependencies(false);
        }
    }, []);

    const loadPrescriptions = useCallback(async (term = '') => {
        const trimmedTerm = term.trim();

        if (trimmedTerm.length < MIN_SEARCH_LENGTH) {
            setPrescriptions([]);
            setSelectedPrescription(null);
            setHasSearched(false);
            return;
        }

        setLoadingPrescriptions(true);
        setHasSearched(true);
        try {
            const encoded = `?search=${encodeURIComponent(trimmedTerm)}`;
            const response = await fetchData(`/dispensing/pending-prescriptions${encoded}`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                const payload = response?.data?.data ?? response?.data ?? [];
                setPrescriptions(payload);

                if (payload.length === 0) {
                    setSelectedPrescription(null);
                } else {
                    const newSelection = payload[0];
                    setSelectedPrescription(newSelection);
                    resetForm(dependencies);
                }
            }
        } catch (error) {
            console.error('Error loading prescriptions:', error);
        } finally {
            setLoadingPrescriptions(false);
        }
    }, [dependencies, resetForm]);

    useEffect(() => {
        loadDependencies();
    }, [loadDependencies]);

    useEffect(() => {
        // Only reset form when a NEW prescription is selected (not when batches change)
        if (selectedPrescription && dependencies.organization_branches.length > 0 && !formState.organization_branch_pk) {
            resetForm(dependencies);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPrescription]);

    useEffect(() => {
        const checkTrainingCompletion = async () => {
            try {
                const response = await fetchData('/training_material/check-completion', 'GET', true);
                if (response && !response.data.completed) {
                    // Differentiate between PENDING_TRAINING and PENDING_RECYCLING_TRAINING
                    const isRecyclingTraining = accountStatus === 10;

                    const title = isRecyclingTraining
                        ? intl.get('training_recycling_required').d('Treinamento de Reciclagem Obrigatório')
                        : intl.get('training_required').d('Treinamento Obrigatório');

                    const message = isRecyclingTraining
                        ? intl.get('training_recycling_required_message').d(
                            'Necessário fazer o treinamento de reciclagem, devido ao excesso de dispensacões onde a data da dispensacão é diferente da data do registro no sistema. Complete o treinamento de reciclagem \'Treinamento Lenalidomida\' para restaurar o acesso à dispensação.'
                        )
                        : intl.get('training_required_message').d(
                            'Você precisa concluir o treinamento obrigatório para ter acesso completo ao sistema. Complete o treinamento \'Prescrição e Dispensação\' antes de dispensar medicamentos.'
                        );

                    setTrainingWarningTitle(title);
                    setTrainingWarningMessage(message);
                    setShowTrainingWarningModal(true);
                }
            } catch (error) {
                console.error('Error checking training completion:', error);
            }
        };
        checkTrainingCompletion();
    }, [accountStatus]);

    useEffect(() => {
        const fetchAccountStatus = async () => {
            try {
                const response = await fetchData('/account/status', 'GET', true);
                if (response.status === HTTP_STATUS.OK_200) {
                    setAccountStatus(response.data?.status);
                }
            } catch (error) {
                console.error('Error fetching account status:', error);
            }
        };
        fetchAccountStatus();
    }, []);

    const fetchTermDocument = async () => {
        setLoadingTerm(true);
        try {
            const response = await fetchData("/legal-document/public/dosage_rectification_term", 'GET', false);
            if (response.status === HTTP_STATUS.OK_200) {
                setTermDocument(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch dosage rectification term:", error);
        } finally {
            setLoadingTerm(false);
        }
    };

    const handleSelectPrescription = (prescription) => {
        setSelectedPrescription(prescription);
    };

    const handleInputChange = (field) => (event) => {
        const value = event?.target?.value ?? event;
        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleCheckboxChange = (field) => (event) => {
        const checked = event?.target?.checked ?? event;
        setFormState((prev) => ({
            ...prev,
            [field]: checked,
        }));
    };

    const handleInstitutionChange = async (event) => {
        const value = event?.target?.value ?? event;

        // Update form state with new institution
        setFormState((prev) => ({
            ...prev,
            organization_branch_pk: value,
        }));

        // Clear batch selections and batches list immediately when institution changes
        setBatchSelections([{
            batch_pk: null,
            box_count: 1,
            capsule_count: '',
            capsule_count_readonly: false,
            batch_details: null,
            batch_not_listed: false,
            global_options: [],
            loading_global: false,
        }]);
        setDependencies((prev) => ({
            ...prev,
            batches: [],
        }));

        // Fetch batches for selected institution
        if (!value) {
            return;
        }

        setLoadingBatches(true);
        try {
            const response = await fetchData(
                `/dispensing/batches-by-institution?organization_branch_pk=${value}`,
                'GET',
                true
            );

            if (response.status === HTTP_STATUS.OK_200) {
                const batches = response?.data?.data ?? [];

                // Sort by expiration date ASC (FEFO - First Expired, First Out)
                const sortedBatches = batches.sort((a, b) =>
                    new Date(a.expiration) - new Date(b.expiration)
                );

                setDependencies((prev) => ({
                    ...prev,
                    batches: sortedBatches,
                }));
            }
        } catch (error) {
            console.error('Error loading batches for institution:', error);
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get('error_loading_batches').d('Failed to load batches for selected institution.'),
            });
        } finally {
            setLoadingBatches(false);
        }
    };

    // Search global batches for "batch not listed" mode
    const searchGlobalBatches = useCallback(async (index, searchTerm) => {
        if (!searchTerm || searchTerm.length < 3 || !formState.organization_branch_pk) {
            return;
        }
        setBatchSelections(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], loading_global: true };
            return updated;
        });
        try {
            const response = await fetchData(
                `/dispensing/search-all-batches?q=${encodeURIComponent(searchTerm)}&organization_branch_pk=${formState.organization_branch_pk}`,
                'GET',
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setBatchSelections(prev => {
                    const updated = [...prev];
                    if (updated[index]) {
                        updated[index] = { ...updated[index], global_options: response?.data?.data ?? [], loading_global: false };
                    }
                    return updated;
                });
            }
        } catch (error) {
            console.error('Error searching global batches:', error);
            setBatchSelections(prev => {
                const updated = [...prev];
                if (updated[index]) {
                    updated[index] = { ...updated[index], loading_global: false };
                }
                return updated;
            });
        }
    }, [formState.organization_branch_pk]);

    // Check if user is approved at selected institution
    useEffect(() => {
        const institutionPk = Number(formState.organization_branch_pk);
        if (institutionPk && institutionStatuses.length > 0) {
            const approvalStatus = getInstitutionStatus(institutionPk);
            setInstitutionApprovalStatus(approvalStatus);

            // User is not approved if no approval record or not active status
            const isNotApproved = !isApprovedAtInstitution(institutionPk);
            setSelectedInstitutionInactive(isNotApproved);
        } else {
            setInstitutionApprovalStatus(null);
            setSelectedInstitutionInactive(false);
        }
    }, [formState.organization_branch_pk, institutionStatuses, isApprovedAtInstitution, getInstitutionStatus]);

    // Check institution's own registration status
    useEffect(() => {
        const institutionPk = Number(formState.organization_branch_pk);
        if (institutionPk && dependencies.organization_branches.length > 0) {
            const branch = dependencies.organization_branches.find(b => b.pk === institutionPk);
            const institutionStatus = branch?.institution_status;
            const isNotApproved = institutionStatus !== undefined && institutionStatus !== InstitutionStatusEnum.APPROVED;
            setSelectedInstitutionNotApproved(isNotApproved);
            setSelectedInstitutionOwnStatusLabel(isNotApproved ? InstitutionStatus.getText(institutionStatus) : '');
        } else {
            setSelectedInstitutionNotApproved(false);
            setSelectedInstitutionOwnStatusLabel('');
        }
    }, [formState.organization_branch_pk, dependencies.organization_branches]);

    const prescriptionExpired = useMemo(() => {
        if (!selectedPrescription) {
            return false;
        }
        return isExpired(selectedPrescription.expiration_date);
    }, [selectedPrescription]);

    const isPrescriptionDispensed = useMemo(() => {
        if (!selectedPrescription) {
            return false;
        }
        return selectedPrescription.prescription_status === PRESCRIPTION_STATUS.DISPENSED;
    }, [selectedPrescription]);

    const isPrescriptionNotDispensed = useMemo(() => {
        if (!selectedPrescription) {
            return false;
        }
        return selectedPrescription.prescription_status === PRESCRIPTION_STATUS.NOT_DISPENSED;
    }, [selectedPrescription]);

    const isPatientInactive = useMemo(() => {
        if (!selectedPrescription) {
            return false;
        }
        return selectedPrescription.patient_status === PatientStatusEnum.INACTIVE;
    }, [selectedPrescription]);

    const isRetroactiveDispensationValid = useMemo(() => {
        if (!isPatientInactive) return true;
        if (!formState.dispensation_date || !dayjs.isDayjs(formState.dispensation_date) || !formState.dispensation_date.isValid()) return false;
        const inactivatedAt = selectedPrescription?.patient_inactivated_at ? dayjs(selectedPrescription.patient_inactivated_at) : null;
        if (!inactivatedAt || !inactivatedAt.isValid()) return true;
        return formState.dispensation_date.isBefore(inactivatedAt, 'day');
    }, [isPatientInactive, formState.dispensation_date, selectedPrescription]);

    // Calculate quantities and validate against prescribed amount (with mg equivalence)
    const quantityValidation = useMemo(() => {
        if (!selectedPrescription) {
            return {
                isValid: true,
                prescribedQuantity: 0,
                selectedQuantity: 0,
                prescribedTotalMg: 0,
                selectedTotalMg: 0,
                exceedsLimit: false,
                showAlternativeWarning: false,
                hasAlternativeStrengths: false,
                batchStrength: null,
                hasDuplicateBatches: false,
                hasEmptyBatches: false,
            };
        }

        // Prescribed values
        const prescribedQuantity = selectedPrescription.medicine_quantity_value || 0;
        const prescribedStrength = selectedPrescription.medicine_dose_strength || 0;
        const prescribedDuration = selectedPrescription.medicine_duration || 1;
        const prescribedTotalMg = prescribedQuantity * prescribedStrength;
        // Daily dose = capsules/day × strength. E.g. 2×5mg/day = 10mg/day.
        const prescribedDailyDose = prescribedDuration > 0 && prescribedQuantity > 0
            ? (prescribedQuantity / prescribedDuration) * prescribedStrength
            : prescribedStrength;

        // Selected values (multi-batch)
        const selectedQuantity = calculateTotalCapsules();
        const selectedTotalMg = calculateTotalMg();

        // Validate batch selections
        const batchPks = batchSelections.map(s => s.batch_pk).filter(Boolean);
        const hasDuplicateBatches = new Set(batchPks).size !== batchPks.length;
        const hasEmptyBatches = batchSelections.some(s => !s.batch_pk);

        // Get all strengths from selected batches
        const strengths = batchSelections
            .map(s => s.batch_details ? extractStrengthFromName(s.batch_details.part_name) : null)
            .filter(Boolean);
        const allSameStrength = new Set(strengths).size === 1;
        const batchStrength = strengths.length > 0 ? strengths[0] : prescribedStrength;

        // Check if alternative dispensation is allowed
        const allowAlternative = selectedPrescription.allow_alternative_dispensation || false;

        // Calculate daily dose strength sum: sum(box_count × strength) for each batch
        // Each box contributes 1 capsule per daily dose, so the sum represents the daily dose in mg
        const selectedStrengthSum = batchSelections.reduce((sum, s) => {
            if (!s.batch_details) return sum;
            const strength = extractStrengthFromName(s.batch_details.part_name);
            return sum + (s.box_count || 0) * strength;
        }, 0);

        // Validation logic
        const isExactMatch = (selectedQuantity === prescribedQuantity) && allSameStrength && (batchStrength === prescribedStrength);
        const isEquivalent = selectedTotalMg === prescribedTotalMg;
        const hasMixedStrengths = !allSameStrength && strengths.length > 1;

        // Check if all selected strengths match the prescribed strength
        // This distinguishes "same drug, more boxes" from "alternative dispensation"
        const allStrengthsMatchPrescribed = strengths.length > 0 && strengths.every(s => s === prescribedStrength);

        // Daily dose check:
        // - Same strength as prescribed: boxes just extend supply, daily dose is always valid
        // - Different strength(s): each box contributes 1 cap/day, so daily dose = sum(box_count × strength)
        //   Must equal the prescribed daily dose (capsules/day × strength), not just the capsule strength.
        //   E.g. 2×5mg/day (10mg/day) accepts 1×10mg/day (10mg/day).
        const isDailyDoseMatch = allStrengthsMatchPrescribed
            ? true
            : selectedStrengthSum === prescribedDailyDose;
        const exceedsLimit = selectedTotalMg > prescribedTotalMg;

        // Days calculation:
        // - Same strength as prescribed: 1 cap/day, capsules = days
        // - Different strength(s): patient takes multiple caps/day (one per box),
        //   so days = totalCapsules / totalDailyCaps. Compare against prescribedDuration (days),
        //   not prescribedQuantity (total capsules), which are different units.
        let daysDifference;
        if (!allStrengthsMatchPrescribed && strengths.length > 0) {
            const totalDailyCaps = batchSelections.reduce((sum, s) => sum + (s.box_count || 0), 0);
            const dispensedDays = totalDailyCaps > 0 ? selectedQuantity / totalDailyCaps : 0;
            daysDifference = Math.abs(dispensedDays - prescribedDuration);
        } else {
            // Same strength: 1 capsule/day, so capsules = days
            daysDifference = Math.abs(selectedQuantity - prescribedQuantity);
        }
        const maxAllowedDiff = packagingMaxDiff[String(prescribedStrength)] || 0;
        const withinOneBox = maxAllowedDiff > 0 && daysDifference <= maxAllowedDiff;

        // Check if prescribed quantity is achievable with whole commercial boxes of the prescribed strength.
        // If it is, packaging adjustment is NOT allowed — the prescribed quantity can be dispensed exactly.
        // Uses modulo to handle multiples: e.g. 42 caps with 21-cap boxes → 2 whole boxes → commercial.
        const availableSizes = packagingAvailableSizes[String(prescribedStrength)] || [];
        const prescribedQtyIsCommercial = availableSizes.length > 0 && availableSizes.some(size => prescribedQuantity % size === 0);

        // Determine validity based on alternative dispensation flag
        let isValid;
        let needsPackagingAdjustment = false;
        if (allowAlternative) {
            // Daily dose must match prescribed strength AND total mg must match (correct treatment duration)
            isValid = isDailyDoseMatch && isEquivalent && !hasDuplicateBatches && !hasEmptyBatches;

            // Rule 3: If daily dose is correct but quantity differs, commercial adequacy may apply
            // But NOT if the prescribed quantity already exists as a commercial presentation
            const strengthCompatible = allStrengthsMatchPrescribed || isDailyDoseMatch;
            if (!isValid && !prescribedQtyIsCommercial && strengthCompatible && !isEquivalent && !hasDuplicateBatches && !hasEmptyBatches && withinOneBox) {
                needsPackagingAdjustment = true;
            }
        } else {
            isValid = isExactMatch && !hasDuplicateBatches && !hasEmptyBatches;

            // Rule 3: If strength matches but quantity differs, commercial adequacy may apply
            // But NOT if the prescribed quantity already exists as a commercial presentation
            if (!isValid && !prescribedQtyIsCommercial && !hasDuplicateBatches && !hasEmptyBatches && withinOneBox) {
                const strengthMatches = strengths.length > 0 && strengths.every(s => s === prescribedStrength);
                if (strengthMatches && selectedQuantity !== prescribedQuantity) {
                    needsPackagingAdjustment = true;
                }
            }
        }

        // Alternative strengths: using different strength(s) from prescribed with matching daily dose
        // Controls: adjusted cycle text (instructions) + alternative dispensation checkbox
        const hasAlternativeStrengths = allowAlternative && isDailyDoseMatch && !allStrengthsMatchPrescribed && !isExactMatch;

        // Show "equivalent combination" alert only for Rule 2 (total mg matches, no packaging adjustment)
        const showAlternativeWarning = hasAlternativeStrengths && isEquivalent && !needsPackagingAdjustment;

        return {
            isValid,
            prescribedQuantity,
            selectedQuantity,
            prescribedTotalMg,
            selectedTotalMg,
            prescribedStrength,
            prescribedDailyDose,
            selectedStrengthSum,
            batchStrength,
            exceedsLimit,
            isExactMatch,
            isEquivalent,
            isDailyDoseMatch,
            hasMixedStrengths,
            allowAlternative,
            showAlternativeWarning,
            hasAlternativeStrengths,
            needsPackagingAdjustment,
            daysDifference,
            hasDuplicateBatches,
            hasEmptyBatches,
        };
    }, [selectedPrescription, batchSelections, calculateTotalCapsules, calculateTotalMg, packagingMaxDiff, packagingAvailableSizes]);

    const isFormValid = useMemo(() => {
        const termAccepted = !quantityValidation.hasAlternativeStrengths || formState.dosage_rectification_term_accepted;
        const allBatchesSelected = batchSelections.length > 0 && batchSelections.every(s => s.batch_pk);
        const allPositiveQuantities = batchSelections.every(s => s.box_count > 0);
        const allCapsuleCounts = batchSelections.every(s => s.capsule_count && Number(s.capsule_count) > 0);

        // Packaging adjustment: valid if user checked the acknowledgment checkbox
        const packagingAdjustmentOk = !quantityValidation.needsPackagingAdjustment || formState.allow_packaging_adjustment;
        const quantityOk = quantityValidation.isValid || (quantityValidation.needsPackagingAdjustment && formState.allow_packaging_adjustment);

        return (
            selectedPrescription !== null &&
            formState.organization_branch_pk &&
            formState.dispensation_date &&
            dayjs.isDayjs(formState.dispensation_date) &&
            formState.dispensation_date.isValid() &&
            allBatchesSelected &&
            allPositiveQuantities &&
            allCapsuleCounts &&
            formState.ppg_counseling_confirmed &&
            formState.liability_term_received &&
            quantityOk &&
            termAccepted &&
            packagingAdjustmentOk &&
            !selectedInstitutionInactive &&
            !selectedInstitutionNotApproved &&
            isRetroactiveDispensationValid
        );
    }, [selectedPrescription, formState, batchSelections, quantityValidation.isValid, quantityValidation.hasAlternativeStrengths, quantityValidation.needsPackagingAdjustment, selectedInstitutionInactive, selectedInstitutionNotApproved, isRetroactiveDispensationValid]);

    const insufficientStockBatches = useMemo(() => {
        return batchSelections.filter(s => {
            if (!s.batch_details || !s.batch_pk) return false;
            return Number(s.box_count) > (s.batch_details.stock || 0);
        }).map(s => ({
            batchNumber: s.batch_details.number,
            requested: Number(s.box_count),
            available: s.batch_details.stock || 0,
            notAtInstitution: s.batch_not_listed && s.batch_details.has_location === false,
        }));
    }, [batchSelections]);

    const hasInsufficientStock = insufficientStockBatches.length > 0;

    const adjustedCycleText = useMemo(() => {
        if (!quantityValidation.hasAlternativeStrengths) return '';
        const desc = selectedPrescription?.dosage_description || '';
        if (!desc) return '';

        // Extract cycle part: everything from "POR X DIAS" onwards (after "AO DIA")
        const aoDiaIdx = desc.toUpperCase().indexOf('AO DIA');
        if (aoDiaIdx === -1) return '';
        const cyclePart = desc.substring(aoDiaIdx + 7).trim();

        // Extract prescribed treatment days from cycle part
        const daysMatch = cyclePart.match(/POR\s+(\d+)\s+DIAS/i);
        const prescribedDays = daysMatch ? parseInt(daysMatch[1]) : 0;
        if (!prescribedDays) return '';

        // Group total capsules and box_count per strength
        const capsPerStrength = {};
        let totalBoxCount = 0;
        batchSelections.forEach(s => {
            if (!s.batch_details) return;
            const strength = extractStrengthFromName(s.batch_details.part_name);
            const caps = (s.box_count || 0) * (Number(s.capsule_count) || 0);
            totalBoxCount += (s.box_count || 0);
            if (strength) {
                capsPerStrength[strength] = (capsPerStrength[strength] || 0) + caps;
            }
        });

        // Always use prescribed days for the cycle text
        // Packaging adjustment means extra/fewer capsules, not a different treatment cycle
        const treatmentDays = prescribedDays;

        // Compute daily capsules per strength
        const strengths = Object.keys(capsPerStrength).map(Number).sort((a, b) => a - b);
        if (strengths.length === 0) return '';

        let prefix;
        if (strengths.length === 1) {
            const s = strengths[0];
            const daily = Math.floor(capsPerStrength[s] / treatmentDays) || 1;
            const capWord = daily === 1 ? 'CÁPSULA' : 'CÁPSULAS';
            prefix = `ADMINISTRAR ${daily} ${capWord} DE ${s}MG`;
        } else {
            const parts = strengths.map(s => {
                const daily = Math.floor(capsPerStrength[s] / treatmentDays) || 1;
                const capWord = daily === 1 ? 'CÁPSULA' : 'CÁPSULAS';
                return `${daily} ${capWord} DE ${s}MG`;
            });
            prefix = `ADMINISTRAR ${parts.join(' MAIS ')}`;
        }

        return `${prefix} AO DIA ${cyclePart}`;
    }, [selectedPrescription, batchSelections, quantityValidation.hasAlternativeStrengths, quantityValidation.needsPackagingAdjustment]);

    const handleConfirmClick = async () => {
        if (!isFormValid) {
            Swal.fire({
                icon: 'warning',
                title: intl.get('Validation').d('Validation'),
                text: intl.get('fill_all_required_fields').d('Please fill all required fields.'),
            });
            return;
        }

        if (formState.allow_packaging_adjustment && quantityValidation.needsPackagingAdjustment) {
            const isSurplus = quantityValidation.selectedTotalMg > quantityValidation.prescribedTotalMg;
            const diffDays = quantityValidation.daysDifference || Math.abs(quantityValidation.selectedQuantity - quantityValidation.prescribedQuantity);
            const typeLabel = isSurplus
                ? intl.get("Dispensation.PackagingAdjustment.Surplus").d("Surplus")
                : intl.get("Dispensation.PackagingAdjustment.Deficit").d("Deficit");

            const pauseDays = selectedPrescription?.dosage_pause_days || 0;
            const dispensedCapsules = quantityValidation.selectedQuantity;
            const prescriptionDate = selectedPrescription?.prescription_date
                ? dayjs(selectedPrescription.prescription_date)
                : dayjs();

            let modalHtml;
            if (isSurplus) {
                const totalDays = dispensedCapsules + pauseDays - 1;
                const limitDate = prescriptionDate.add(totalDays, 'day').format('DD/MM/YYYY');
                modalHtml = `
                    <p>${intl.get('Dispensation.PackagingAdjustment.ConfirmMessageSurplus')
                        .d('The quantity to be dispensed exceeds the prescription due to the available commercial presentation. To ensure there is no leftover medication, a new prescription may only be issued to the patient after ' + limitDate + '. Do you want to confirm the dispensation?')
                        .replace('{{date}}', limitDate)
                    }</p>
                    <p><strong>${typeLabel}: ${diffDays} ${intl.get("Dispensation.Days").d("day(s)")}</strong></p>
                `;
            } else {
                const xx = dispensedCapsules + pauseDays - 1;
                const startDate = prescriptionDate.add(xx, 'day').format('DD/MM/YYYY');
                const endDate = prescriptionDate.add(xx + 1, 'day').format('DD/MM/YYYY');
                modalHtml = `
                    <p>${intl.get('Dispensation.PackagingAdjustment.ConfirmMessageDeficit')
                        .d('In compliance with RDC No. 735/2022 and to ensure continuity of treatment with Lenalidomide as prescribed, the patient should return to the healthcare facility between ' + startDate + ' and ' + endDate + ' for clinical evaluation and possible issuance of a new prescription notification to continue the proposed treatment.')
                        .replace('{{startDate}}', startDate)
                        .replace('{{endDate}}', endDate)
                    }</p>
                    <p><strong>${typeLabel}: ${diffDays} ${intl.get("Dispensation.Days").d("day(s)")}</strong></p>
                `;
            }

            const result = await Swal.fire({
                icon: 'warning',
                title: intl.get('Dispensation.PackagingAdjustment.ConfirmTitle').d('Confirm Commercial Adequacy'),
                html: modalHtml,
                showCancelButton: true,
                confirmButtonText: intl.get('Dispensation.ProceedAnyway').d('Proceed'),
                cancelButtonText: intl.get('Cancel').d('Cancel'),
                confirmButtonColor: '#ed6c02',
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        if (hasInsufficientStock) {
            const batchList = insufficientStockBatches.map(b => {
                const notAtInst = b.notAtInstitution
                    ? ` <em style="color: #d32f2f;">(${intl.get('Dispensation.BatchNotAtInstitution').d('This batch is not registered at this institution.')})</em>`
                    : '';
                return `<li><strong>${b.batchNumber}</strong>: ${intl.get('Dispensation.RequestedVsAvailable').d('Requested')}: ${b.requested}, ${intl.get('available_stock').d('Available')}: ${b.available}${notAtInst}</li>`;
            }).join('');

            const result = await Swal.fire({
                icon: 'warning',
                title: intl.get('Dispensation.InsufficientStockTitle').d('Insufficient Stock'),
                html: `
                    <p>${intl.get('Dispensation.InsufficientStockMessage').d(
                        'One or more batches do not have sufficient stock for this dispensation. Proceeding will generate a notification to Eurofarma.'
                    )}</p>
                    <ul style="text-align: left;">${batchList}</ul>
                    <p><strong>${intl.get('Dispensation.InsufficientStockConfirm').d(
                        'Do you want to proceed with the dispensation?'
                    )}</strong></p>
                `,
                showCancelButton: true,
                confirmButtonText: intl.get('Dispensation.ProceedAnyway').d('Proceed'),
                cancelButtonText: intl.get('Cancel').d('Cancel'),
                confirmButtonColor: '#d33',
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        setShowDoubleBlindModal(true);
    };

    const handleSubmit = async () => {
        if (!isFormValid || !selectedPrescription) {
            return;
        }

        if (!formState.dispensation_date || !dayjs.isDayjs(formState.dispensation_date) || !formState.dispensation_date.isValid()) {
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get('dispensation_date_required').d('A dispensation date is required.'),
            });
            return;
        }

        // Validate institution is selected
        const institutionPk = Number(formState.organization_branch_pk);
        if (!institutionPk || institutionPk === 0) {
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get('institution_required').d('Please select an institution.'),
            });
            return;
        }

        // Validate capsule_count per batch
        const invalidBatch = batchSelections.find(s => !s.capsule_count || Number(s.capsule_count) <= 0);
        if (invalidBatch) {
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get("Dispensation.InvalidCapsuleCount").d("Please enter a valid capsule count greater than zero"),
            });
            return;
        }

        const payload = {
            prescription_pk: selectedPrescription.prescription_pk,
            dispensed_dose_strength: Number(selectedPrescription.medicine_dose_strength || 0),
            dispensed_dose_strength_unit: selectedPrescription.medicine_dose_strength_unit || StrengthUnit.MG,
            batches: batchSelections.map(s => ({
                batch_pk: Number(s.batch_pk),
                box_count: Number(s.box_count),
                capsule_count: Number(s.capsule_count)
            })),
            pharmacist_pk: 0,
            organization_branch_pk: institutionPk,
            status: DispensingStatusEnum.DISPENSED,
            ppg_counseling_confirmed: formState.ppg_counseling_confirmed,
            liability_term_received: formState.liability_term_received,
            dosage_rectification_term_accepted: formState.dosage_rectification_term_accepted,
            delivery_date: formState.dispensation_date.format('YYYY-MM-DD'),
            allow_insufficient_stock: hasInsufficientStock,
            batch_not_listed: batchSelections.some(s => s.batch_not_listed),
            allow_packaging_adjustment: formState.allow_packaging_adjustment,
        };

        setIsSubmitting(true);

        // Pre-open window now (in user activation context) before any awaits,
        // so the popup isn't blocked when the backend response takes longer
        const preOpenedReceiptWindow = window.open('', '_blank');

        try {
            const response = await fetchData('/dispensing/complete', 'POST', true, payload, false, true);

            if (response.status === 403) {
                const isRecyclingTraining = accountStatus === 10;
                if (isRecyclingTraining) {
                    Swal.fire({
                        icon: 'warning',
                        title: intl.get('training_recycling_required').d('Recycling Training Required'),
                        text: intl.get('training_recycling_required_message').d(
                            'Recycling training is required due to excess dispensations where the dispensation date differs from the registration date. Complete the recycling training \'Lenalidomide Training\' to restore access to dispensing.'
                        ),
                    });
                } else {
                    const errorDetail = response.data?.detail;
                    Swal.fire({
                        icon: 'warning',
                        title: errorDetail
                            ? intl.get('AccessDenied').d('Access Denied')
                            : intl.get('training_required').d('Mandatory Training'),
                        text: errorDetail || intl.get('training_required_message').d(
                            'You need to complete the mandatory training to have full access to the system. Complete the \'Prescription and Dispensation\' training before dispensing medications.'
                        ),
                    });
                }
                if (preOpenedReceiptWindow) preOpenedReceiptWindow.close();
                return;
            }

            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                const dispensingPk = response.data?.data?.pk;
                const prescriptionPk = response.data?.data?.prescription_pk;

                if (dispensingPk) {
                    openDispensingReceiptForPrint(dispensingPk, preOpenedReceiptWindow);
                } else {
                    if (preOpenedReceiptWindow) preOpenedReceiptWindow.close();
                    if (prescriptionPk) openPrescriptionPdfForPrint(prescriptionPk);
                }

                Swal.fire({
                    icon: 'success',
                    title: intl.get('DispensationSuccess').d('Dispensation completed'),
                    text: intl.get('DispensationSuccessMessage').d('The prescription was dispensed successfully.'),
                });
                await loadPrescriptions(searchTerm);
            } else {
                if (preOpenedReceiptWindow) preOpenedReceiptWindow.close();
                const detail = response?.data?.detail || intl.get('DispensationErrorGeneric').d('Failed to complete dispensing.');
                Swal.fire({
                    icon: 'error',
                    title: intl.get('DispensationError').d('Error'),
                    text: detail,
                });
            }
        } catch (error) {
            if (preOpenedReceiptWindow) preOpenedReceiptWindow.close();
            console.error('Submission error:', error);
            Swal.fire({
                icon: 'error',
                title: intl.get('DispensationError').d('Error'),
                text: intl.get('DispensationErrorGeneric').d('Failed to complete dispensing.'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleJustificationSubmit = async () => {
        if (!selectedPrescription || !justification.trim()) {
            return;
        }

        const payload = {
            prescription_pk: selectedPrescription.prescription_pk,
            justification: justification.trim(),
        };

        setIsSubmitting(true);
        try {
            const response = await fetchData('/dispensing/not-dispensed', 'POST', true, payload, false, true);

            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: 'success',
                    title: intl.get('Dispensation.JustificationSuccessTitle').d('Justification submitted'),
                    text: intl.get('Dispensation.JustificationSuccessMessage').d('The justification has been successfully recorded.'),
                });
                setShowJustificationModal(false);
                setJustification('');
                await loadPrescriptions(searchTerm);
            } else {
                const detail = response?.data?.detail || intl.get('Dispensation.JustificationErrorGeneric').d('Failed to submit justification.');
                Swal.fire({
                    icon: 'error',
                    title: intl.get('Error').d('Error'),
                    text: detail,
                });
            }
        } catch (error) {
            console.error('Justification submission error:', error);
            Swal.fire({
                icon: 'error',
                title: intl.get('Error').d('Error'),
                text: intl.get('Dispensation.JustificationErrorGeneric').d('Failed to submit justification.'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSearch = () => {
        if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
            Swal.fire({
                icon: 'info',
                title: intl.get('SearchValidation').d('Search validation'),
                text: intl.get('Dispensation.MinSearchLength', { minLength: MIN_SEARCH_LENGTH }).d("Please enter at least {minLength} characters to search."),
            });
            return;
        }
        loadPrescriptions(searchTerm);
    };

    const renderPrescriptionHeader = (prescription) => (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={0.5}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {prescription.patient_name}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
                {prescription.prescription_status === PRESCRIPTION_STATUS.DISPENSED && (
                    <Chip
                        size="small"
                        color="info"
                        label={intl.get('Dispensation.DispensedChip').d('Dispensed')}
                    />
                )}
                {prescription.prescription_status === PRESCRIPTION_STATUS.NOT_DISPENSED && (
                    <Chip
                        size="small"
                        color="warning"
                        label={intl.get('NotDispensed').d('Not Dispensed')}
                    />
                )}
                <Chip
                    size="small"
                    color="primary"
                    label={prescription.prescription_identifier}
                />
            </Stack>
        </Stack>
    );

    const signatureActionDescription = useMemo(() => {
        if (!selectedPrescription) return '';

        const totalCapsules = calculateTotalCapsules();
        const totalBoxes = calculateTotalBoxes();

        // Patient Information
        const patientInfo = [
            `${intl.get('Patient').d('Patient')}: ${selectedPrescription.patient_name}`,
            `${intl.get('prescription_identifier').d('Prescription ID')}: #${selectedPrescription.prescription_identifier}`
        ].join('\n');

        // Prescribed
        const prescribed = `\n${intl.get('Prescribed').d('Prescribed')}:\n${selectedPrescription.dosage_description}`;

        // Being Dispensed
        const totalMg = calculateTotalMg();
        const beingDispensed = [
            `\n${intl.get('Being_Dispensed').d('Being Dispensed')}:`,
            `${totalBoxes} ${intl.get('boxes').d('box(es)')} = ${totalCapsules} ${intl.get('capsules').d('capsules')} (${totalMg}mg)`
        ];

        // Add each batch
        batchSelections.forEach((selection, index) => {
            if (selection.batch_details) {
                const capCount = Number(selection.capsule_count) || 0;
                const strength = extractStrengthFromName(selection.batch_details.part_name);
                const batchCapsules = selection.box_count * capCount;
                beingDispensed.push(
                    `\n${intl.get('Batch').d('Batch')} ${index + 1}: ${selection.batch_details.number}`,
                    `  ${intl.get('Product').d('Product')}: ${selection.batch_details.part_name}`,
                    `  ${intl.get('Quantity').d('Quantity')}: ${selection.box_count} ${intl.get('boxes').d('box(es)')} × ${capCount} = ${batchCapsules} ${intl.get('capsules').d('capsules')} × ${strength}mg = ${batchCapsules * strength}mg`,
                    `  ${intl.get('expires').d('Expires')}: ${formatDate(selection.batch_details.expiration)}`
                );
            }
        });

        return [patientInfo, prescribed, beingDispensed.join('\n')].join('\n');
    }, [selectedPrescription, batchSelections, calculateTotalCapsules, calculateTotalBoxes, calculateTotalMg]);

    const isSearchDisabled = searchTerm.trim().length < MIN_SEARCH_LENGTH;

    return (
        <DmsPage name={intl.get('Dispensation').d('Dispensation')}>
            <Grid container spacing={{ xs: 2, md: 3 }}>
                {/* Left Column - Search and Prescriptions List */}
                <Grid item xs={12} md={4}>
                    <DmsAltBox.Card>
                        <DmsAltBox.Header>
                            <Typography variant="h6">
                                {intl.get('Dispensation.PendingPrescriptions').d('Pending prescriptions')}
                            </Typography>
                        </DmsAltBox.Header>
                        <DmsAltBox.Body>
                            <Box sx={{ mb: 2 }}>
                                <DmsTextField
                                    name="search"
                                    placeholder={intl.get('Dispensation.SearchPlaceholder').d('Search by patient, identifier or CPF')}
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' && !isSearchDisabled) {
                                            handleSearch();
                                        }
                                    }}
                                    fullWidth
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <DmsButton
                                        variant="primary"
                                        text={intl.get('Search').d('Search')}
                                        onClick={handleSearch}
                                        disabled={loadingPrescriptions || isSearchDisabled}
                                    />
                                    <DmsButton
                                        variant="secondary"
                                        text={intl.get('Clear').d('Clear')}
                                        onClick={() => {
                                            setSearchTerm('');
                                            setPrescriptions([]);
                                            setSelectedPrescription(null);
                                            setHasSearched(false);
                                        }}
                                        disabled={loadingPrescriptions}
                                    />
                                </Stack>
                            </Box>

                            {loadingPrescriptions ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress size={32} aria-label={loadingLabel} />
                                </Box>
                            ) : !hasSearched ? (
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get('Dispensation.SearchToStart').d('Enter at least 3 characters and search to view prescriptions.')}
                                </Typography>
                            ) : prescriptions.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get('Dispensation.NoPendingPrescriptions').d('No pending prescriptions found.')}
                                </Typography>
                            ) : (
                                <List sx={{ maxHeight: { xs: 260, md: 480 }, overflow: 'auto' }}>
                                    {prescriptions.map((prescription) => {
                                        const selected = selectedPrescription?.prescription_pk === prescription.prescription_pk;
                                        return (
                                            <React.Fragment key={prescription.prescription_pk}>
                                                <ListItemButton
                                                    selected={selected}
                                                    onClick={() => handleSelectPrescription(prescription)}
                                                >
                                                    <ListItemText
                                                        primary={renderPrescriptionHeader(prescription)}
                                                        secondary={
                                                            <Typography variant="body2" color="text.secondary">
                                                                {intl.get('Dispensation.PatientIdentifierLabel').d('ID')}: {prescription.patient_identifier}
                                                                {' · '}
                                                                {intl.get('Dispensation.ExpiresOn').d('Expires on')}: {formatDate(prescription.expiration_date)}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                                <Divider component="li" />
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            )}
                        </DmsAltBox.Body>
                    </DmsAltBox.Card>
                </Grid>

                {/* Right Column - Dispensation Form */}
                <Grid item xs={12} md={8}>
                    <DmsAltBox.Card>
                        <DmsAltBox.Header>
                            <Typography variant="h6">
                                {intl.get('Dispensation.DetailsHeader').d('Dispensation details')}
                            </Typography>
                        </DmsAltBox.Header>
                        <DmsAltBox.Body>
                            {loadingDependencies ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress size={32} aria-label={loadingLabel} />
                                </Box>
                            ) : !selectedPrescription ? (
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get('Dispensation.SelectPrescriptionToDispense').d('Select a prescription to start dispensing.')}
                                </Typography>
                            ) : (
                                <Stack spacing={3}>
                                    {/* Prescription Summary */}
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            backgroundColor: 'primary.50',
                                            borderColor: 'primary.200',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <LocalHospital sx={{ fontSize: 24, color: 'primary.main', mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }} xs={12}>
                                                    {selectedPrescription.patient_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {intl.get('Dispensation.PatientIdentifierLabel').d('ID')}: {selectedPrescription.patient_identifier}
                                                    {' · '}
                                                    {intl.get('prescription_identifier').d('Prescription ID')}: #{selectedPrescription.prescription_identifier}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                                                    {selectedPrescription.dosage_description || intl.get("InvalidPrescription").d("Invalid Prescription")}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {intl.get('Dispensation.ExpiresOn').d('Expires on')}: {formatDate(selectedPrescription.expiration_date)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>

                                    {/* Already Dispensed Prescription - Show info alert */}
                                    {isPrescriptionDispensed ? (
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 3,
                                                backgroundColor: 'info.50',
                                                borderColor: 'info.200',
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 2 }}>
                                                {intl.get('Dispensation.AlreadyDispensedTitle').d('Prescription Already Dispensed')}
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                {intl.get('Dispensation.AlreadyDispensedMessage').d(
                                                    'This prescription has already been dispensed. A new dispensation cannot be performed for this prescription.'
                                                )}
                                            </Typography>
                                            {selectedPrescription.dispensation_date && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {intl.get('Dispensation.DispensedOn').d('Dispensed on')}: {formatDate(selectedPrescription.dispensation_date)}
                                                </Typography>
                                            )}
                                        </Paper>
                                    ) : (
                                        <>
                                            {/* Inactive patient warning — retroactive dispensation */}
                                            {isPatientInactive && (
                                                <DmsAlertWarning
                                                    message={`${intl.get('Dispensation.InactivePatientRetroactiveWarning').d(
                                                        'This patient is inactive. Only retroactive dispensations with a date before the inactivation date are allowed.'
                                                    )} ${intl.get('Dispensation.InactivatedOn').d('Inactivated on')}: ${formatDate(selectedPrescription?.patient_inactivated_at)}`}
                                                />
                                            )}

                                            {/* Not dispensed warning — retroactive dispensation required */}
                                            {isPrescriptionNotDispensed && (
                                                <DmsAlertWarning
                                                    message={`${intl.get('Dispensation.NotDispensedRetroactiveWarning').d(
                                                        'This prescription was previously marked as not dispensed. A retroactive dispensation is required. Upon completion, the prescription status will be updated to Dispensed.'
                                                    )}${selectedPrescription.dispensation_justification ? ` ${intl.get('Justification').d('Justification')}: ${selectedPrescription.dispensation_justification}` : ''}`}
                                                />
                                            )}

                                            {/* Expired prescription warning — retroactive dispensation allowed within the validity period */}
                                            {prescriptionExpired && (
                                                <DmsAlertWarning
                                                    message={`${intl.get('Dispensation.ExpiredRetroactiveWarning').d(
                                                        'This prescription has expired. To dispense it, select a delivery date within the prescription validity period (retroactive dispensation).'
                                                    )} ${intl.get('Dispensation.ExpiredOn').d('Expired on')}: ${formatDate(selectedPrescription.expiration_date)}`}
                                                />
                                            )}

                                            {/* Pregnancy Warning */}
                                            {selectedPrescription?.patient_has_pregnancy_potential && (
                                                <DmsAlertWarning
                                                    message={`${intl.get("pregnancy_potential_warning").d(
                                                        "This patient has pregnancy potential. Verify pregnancy test date before dispensing."
                                                    )} ${intl.get("pregnancy_test_date").d("Test date")}: ${formatDate(selectedPrescription?.pregnancy_test_date)}`}
                                                />
                                            )}

                                            <Divider />

                                            {/* Institution Selector */}
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                                    {intl.get("dispensation_institution").d("Dispensation Institution")}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <DmsSelect
                                                            label={`${intl.get("Institution").d("Institution")} *`}
                                                            name="organization_branch_pk"
                                                            value={formState.organization_branch_pk}
                                                            onChange={handleInstitutionChange}
                                                            options={dependencies.organization_branches.map((item) => {
                                                                let displayLabel = item.label;

                                                                // Check INACTIVE first (distinct from "not approved")
                                                                const isInactive = item.institution_status === InstitutionStatusEnum.INACTIVE;
                                                                const isInstitutionApproved = item.institution_status === InstitutionStatusEnum.APPROVED;
                                                                const isUserApproved = item.is_approved;

                                                                if (isInactive) {
                                                                    // Institution was deactivated - show only "Instituição Inativa"
                                                                    displayLabel = `${item.label} - ${intl.get("InactiveInstitution").d("Instituição Inativa")}`;
                                                                } else {
                                                                    // Build status messages for non-inactive institutions
                                                                    const statusMessages = [];
                                                                    if (!isUserApproved) {
                                                                        statusMessages.push(intl.get("UserNotApprovedForInstitution").d("Usuário não aprovado para a instituição"));
                                                                    }
                                                                    if (!isInstitutionApproved) {
                                                                        statusMessages.push(intl.get("InstitutionNotApproved").d("Instituição não aprovada"));
                                                                    }

                                                                    // Concatenate with "e" (and) if both conditions exist
                                                                    if (statusMessages.length > 0) {
                                                                        displayLabel = `${item.label} - ${statusMessages.join(" e ")}`;
                                                                    }
                                                                }

                                                                return {
                                                                    value: item.pk,
                                                                    label: displayLabel
                                                                };
                                                            })}
                                                            fullWidth
                                                            disabled={dependencies.organization_branches.length <= 1}
                                                        />
                                                        {dependencies.organization_branches.length === 1 && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                                {intl.get("dispensation_single_institution_info").d(
                                                                    "This is your only registered institution."
                                                                )}
                                                            </Typography>
                                                        )}
                                                    </Grid>
                                                </Grid>

                                                {selectedInstitutionNotApproved && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <DmsAlertWarning
                                                            message={
                                                                intl.get("Dispensation.InstitutionNotApproved.Warning")
                                                                    .d("This institution is not approved for dispensation. Status: {{status}}")
                                                                    .replace('{{status}}', selectedInstitutionOwnStatusLabel)
                                                            }
                                                        />
                                                    </Box>
                                                )}

                                                {selectedInstitutionInactive && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <DmsAlertWarning
                                                            message={
                                                                institutionApprovalStatus
                                                                    ? intl.get("Dispensation.UserNotApproved.Warning")
                                                                        .d("You are not approved for this institution. Status: {{status}}")
                                                                        .replace('{{status}}', institutionApprovalStatus.status_label || intl.get("Pending").d("Pending"))
                                                                    : intl.get("Dispensation.InstitutionInactive.Warning")
                                                                        .d("The selected institution is inactive. It is not possible to dispense for this institution.")
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            {/* Dispensation Date */}
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                                    {intl.get("dispensation_date").d("Dispensation Date")}
                                                </Typography>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DmsDatePicker
                                                        label={intl.get("dispensation_date_label").d("Date")}
                                                        value={formState.dispensation_date}
                                                        onChange={handleDateChange}
                                                        format="DD/MM/YYYY"
                                                        minDate={selectedPrescription?.prescription_date ? dayjs(selectedPrescription.prescription_date) : null}
                                                        maxDate={
                                                            selectedPrescription?.expiration_date
                                                                && dayjs(selectedPrescription.expiration_date).isBefore(dayjs(), 'day')
                                                                    ? dayjs(selectedPrescription.expiration_date)
                                                                    : dayjs()
                                                        }
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                required: true,
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            {/* Dispensation Fields */}
                                            <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                            {intl.get("dispensation_data").d("Dispensation Data")}
                                        </Typography>
                                        {/* Dynamic Batch Rows */}
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                                {intl.get("dispensation.batches_to_dispense").d("Batches to Dispense")}
                                            </Typography>

                                            {batchSelections.map((selection, index) => (
                                                <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                                                    <Box sx={{ mb: 1 }}>
                                                        <DmsCheckBox
                                                            label={intl.get("Dispensation.BatchNotListed").d("Batch not listed at institution")}
                                                            checked={selection.batch_not_listed}
                                                            onChange={(e) => {
                                                                const checked = e?.target?.checked ?? e;
                                                                handleBatchNotListedChange(index, checked);
                                                            }}
                                                            disabled={!formState.organization_branch_pk}
                                                        />
                                                    </Box>
                                                    <Grid container spacing={2} alignItems="center">
                                                        {/* Batch Selector */}
                                                        <Grid item xs={12} md={7}>
                                                            {selection.batch_not_listed ? (
                                                                <DmsAutocomplete
                                                                    label={`${intl.get("Dispensation.SearchBatchGlobal").d("Search batch (min. 3 characters)")} ${index + 1} *`}
                                                                    value={selection.batch_pk}
                                                                    onChange={(newValue) => handleBatchChange(index, newValue)}
                                                                    options={(selection.global_options || []).map((item) => ({
                                                                        value: item.pk,
                                                                        label: `${item.label} - ${intl.get("Stock").d("Stock")}: ${item.stock || 0}`,
                                                                    }))}
                                                                    getOptionDisabled={(option) =>
                                                                        batchSelections.some((s, i) => i !== index && s.batch_pk === option.value)
                                                                    }
                                                                    minInputLength={3}
                                                                    onSearchChange={(term) => searchGlobalBatches(index, term)}
                                                                    loading={selection.loading_global}
                                                                    fullWidth
                                                                    disabled={!formState.organization_branch_pk}
                                                                />
                                                            ) : (
                                                                <DmsAutocomplete
                                                                    label={`${intl.get("Batch").d("Batch")} ${index + 1} *`}
                                                                    value={selection.batch_pk}
                                                                    onChange={(newValue) => handleBatchChange(index, newValue)}
                                                                    options={dependencies.batches.map((item) => ({
                                                                        value: item.pk,
                                                                        label: `${item.label} - ${intl.get("Stock").d("Stock")}: ${item.stock || 0}`,
                                                                    }))}
                                                                    getOptionDisabled={(option) =>
                                                                        batchSelections.some((s, i) => i !== index && s.batch_pk === option.value)
                                                                    }
                                                                    fullWidth
                                                                    disabled={loadingBatches || !formState.organization_branch_pk}
                                                                />
                                                            )}
                                                        </Grid>

                                                        {/* Capsules per Box */}
                                                        <Grid item xs={5} md={2}>
                                                            <DmsTextField
                                                                label={`${intl.get("Dispensation.CapsuleCount").d("Capsules per Box")} *`}
                                                                type="text"
                                                                value={selection.capsule_count}
                                                                onChange={(e) => handleBatchCapsuleCountChange(index, e?.target?.value ?? e)}
                                                                fullWidth
                                                                disabled={selection.capsule_count_readonly}
                                                                error={selection.capsule_count !== '' && Number(selection.capsule_count) <= 0}
                                                                InputLabelProps={{ shrink: !!selection.capsule_count }}
                                                            />
                                                        </Grid>

                                                        {/* Box Count Input */}
                                                        <Grid item xs={5} md={2}>
                                                            <DmsTextField
                                                                label={`${intl.get("box_count").d("Box Count")} *`}
                                                                type="number"
                                                                value={selection.box_count}
                                                                onChange={(e) => handleBatchBoxCountChange(index, e?.target?.value ?? e)}
                                                                inputProps={{
                                                                    min: 1,
                                                                }}
                                                                fullWidth
                                                            />
                                                        </Grid>

                                                        {/* Remove Button */}
                                                        <Grid item xs={2} md={1}>
                                                            {batchSelections.length > 1 && (
                                                                <IconButton
                                                                    onClick={() => removeBatchSelection(index)}
                                                                    color="error"
                                                                    size="small"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            )}
                                                        </Grid>

                                                        {/* Calculated Totals */}
                                                        <Grid item xs={12} md={2}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {selection.box_count * (Number(selection.capsule_count) || 0)} {intl.get("capsules").d("capsules")}
                                                                {selection.batch_details && (
                                                                    <>
                                                                        <br />
                                                                        {calculateBatchMg(selection)} mg
                                                                    </>
                                                                )}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>

                                                    {/* Batch Details Card */}
                                                    {selection.batch_details && (
                                                        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                                            <Typography variant="caption" display="block">
                                                                <strong>{intl.get("Product").d("Product")}:</strong> {selection.batch_details.part_name}
                                                            </Typography>
                                                            <Typography variant="caption" display="block">
                                                                <strong>{intl.get("ExpirationDate").d("Expiration")}:</strong> {formatDate(selection.batch_details.expiration)}
                                                            </Typography>
                                                            <Typography variant="caption" display="block">
                                                                <strong>{intl.get("available_stock").d("Available")}:</strong> {selection.batch_details.stock || 0} {intl.get("boxes").d("boxes")}
                                                            </Typography>
                                                            {selection.batch_not_listed && selection.batch_details.has_location === false && (
                                                                <Typography variant="caption" display="block" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                                                    {intl.get("Dispensation.BatchNotAtInstitution").d("This batch is not registered at this institution.")}
                                                                </Typography>
                                                            )}
                                                            {Number(selection.box_count) > (selection.batch_details.stock || 0) && (
                                                                <Typography variant="caption" display="block" color="error.main" sx={{ fontWeight: 'bold' }}>
                                                                    {intl.get("Dispensation.StockExceeded").d("Stock exceeded! A notice will be generated.")}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Paper>
                                            ))}

                                            {/* Loading/Error Messages */}
                                            {loadingBatches && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                    {intl.get("loading_batches").d("Loading batches...")}
                                                </Typography>
                                            )}
                                            {!loadingBatches && dependencies.batches.length === 0 && formState.organization_branch_pk && (
                                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 2 }}>
                                                    {intl.get("no_batches_available").d("No batches available for this institution.")}
                                                </Typography>
                                            )}

                                            {/* Add Batch Button */}
                                            <DmsButton
                                                variant="outlined"
                                                text={intl.get("dispensation.add_batch").d("Add Another Batch")}
                                                onClick={addBatchSelection}
                                                disabled={loadingBatches || !formState.organization_branch_pk}
                                                icon={<AddIcon />}
                                            />

                                            {/* Total Summary Card */}
                                            <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.light', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                    {intl.get("dispensation.summary").d("Dispensation Summary")}
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={4}>
                                                        <Typography variant="body2">
                                                            <strong>{intl.get("dispensation.total_boxes").d("Total Boxes")}:</strong> {calculateTotalBoxes()}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="body2">
                                                            <strong>{intl.get("dispensation.total_capsules").d("Total Capsules")}:</strong> {calculateTotalCapsules()}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Typography variant="body2">
                                                            <strong>{intl.get("dispensation.total_dose").d("Total Dose")}:</strong> {calculateTotalMg()} mg
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                {/* Validation Errors */}
                                                {quantityValidation.hasDuplicateBatches && (
                                                    <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 600 }}>
                                                        {intl.get("dispensation.error.duplicate_batches").d("Cannot use the same batch multiple times")}
                                                    </Typography>
                                                )}
                                                {quantityValidation.hasEmptyBatches && (
                                                    <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 600 }}>
                                                        {intl.get("dispensation.error.select_all_batches").d("Please select a batch for all rows")}
                                                    </Typography>
                                                )}
                                            </Paper>
                                        </Box>

                                        {/* Alternative Dispensation Warning */}
                                        {quantityValidation.showAlternativeWarning && (
                                            <Box sx={{ mt: 2 }}>
                                                <DmsAlertWarning
                                                    message={intl.get("Dispensation.AlternativeDispensationWarning").d(
                                                        "Dispensation with equivalent combination authorized by physician"
                                                    )}
                                                />
                                            </Box>
                                        )}

                                        {/* Quantity Validation */}
                                        <Box sx={{ mt: 2 }}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: quantityValidation.exceedsLimit ? 'error.50' :
                                                        quantityValidation.needsPackagingAdjustment ? 'warning.50' :
                                                        quantityValidation.hasAlternativeStrengths ? 'warning.50' : 'info.50',
                                                    borderColor: quantityValidation.exceedsLimit ? 'error.200' :
                                                        quantityValidation.needsPackagingAdjustment ? 'warning.200' :
                                                        quantityValidation.hasAlternativeStrengths ? 'warning.200' : 'info.200',
                                                }}
                                            >
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                                    {intl.get("Dispensation.QuantityInformation").d("Quantity Information")}
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2">
                                                        {intl.get("Dispensation.PrescribedQuantity").d("Prescribed")}: <strong>{quantityValidation.prescribedQuantity} {intl.get("capsules").d("capsules")} × {quantityValidation.prescribedStrength}mg = {quantityValidation.prescribedTotalMg}mg</strong>
                                                    </Typography>
                                                    {batchSelections.filter(s => s.batch_details).length > 1 ? (
                                                        <>
                                                            <Typography variant="body2">
                                                                {intl.get("Dispensation.SelectedQuantity").d("Selected")}:
                                                            </Typography>
                                                            {batchSelections.map((selection, index) => {
                                                                if (!selection.batch_details) return null;
                                                                const capCount = Number(selection.capsule_count) || 0;
                                                                const strength = extractStrengthFromName(selection.batch_details.part_name);
                                                                const batchCapsules = selection.box_count * capCount;
                                                                return (
                                                                    <Typography key={index} variant="body2" sx={{ pl: 1 }}>
                                                                        {intl.get("Batch").d("Batch")} {selection.batch_details.number}: <strong>{selection.box_count} × {capCount} {intl.get("capsules").d("capsules")} × {strength}mg = {batchCapsules * strength}mg</strong>
                                                                    </Typography>
                                                                );
                                                            })}
                                                            <Typography variant="body2" sx={{ pl: 1, fontWeight: 600 }}>
                                                                {intl.get("Total").d("Total")}: <strong>{quantityValidation.selectedQuantity} {intl.get("capsules").d("capsules")} = {quantityValidation.selectedTotalMg}mg</strong>
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {intl.get("Dispensation.SelectedQuantity").d("Selected")}: <strong>{quantityValidation.selectedQuantity} {intl.get("capsules").d("capsules")} × {quantityValidation.batchStrength}mg = {quantityValidation.selectedTotalMg}mg</strong>
                                                        </Typography>
                                                    )}
                                                    {quantityValidation.exceedsLimit && !quantityValidation.needsPackagingAdjustment && (
                                                        <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 600 }}>
                                                            {intl.get("Dispensation.ExceedsLimitWarning").d("Cannot dispense more than prescribed total!")}
                                                        </Typography>
                                                    )}
                                                    {!quantityValidation.isValid && !quantityValidation.exceedsLimit && !quantityValidation.allowAlternative && !quantityValidation.needsPackagingAdjustment && (
                                                        <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 600 }}>
                                                            {intl.get("Dispensation.ExactMatchRequired").d("Exact match required. Quantity and strength must match prescription.")}
                                                        </Typography>
                                                    )}
                                                    {!quantityValidation.isValid && !quantityValidation.exceedsLimit && quantityValidation.allowAlternative && !quantityValidation.isDailyDoseMatch && (
                                                        <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 600 }}>
                                                            {intl.get("Dispensation.DailyDoseMismatch").d("The sum of the selected batch strengths must equal the prescribed strength so the patient takes a consistent daily dose.")}{" "}
                                                            ({intl.get("Dispensation.DailyDoseExpected").d("Expected")}: {quantityValidation.prescribedDailyDose}mg, {intl.get("Dispensation.DailyDoseSelected").d("Selected")}: {quantityValidation.selectedStrengthSum}mg)
                                                        </Typography>
                                                    )}
                                                    {!quantityValidation.isValid && !quantityValidation.exceedsLimit && !quantityValidation.needsPackagingAdjustment && quantityValidation.allowAlternative && quantityValidation.isDailyDoseMatch && !quantityValidation.isEquivalent && (
                                                        <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 600 }}>
                                                            {intl.get("Dispensation.TotalMgMismatch").d("Total mg must match prescription.")}
                                                        </Typography>
                                                    )}
                                                    {quantityValidation.needsPackagingAdjustment && (
                                                        <Paper
                                                            variant="outlined"
                                                            sx={{
                                                                mt: 1,
                                                                p: 1.5,
                                                                backgroundColor: formState.allow_packaging_adjustment ? 'warning.50' : 'info.50',
                                                                borderColor: formState.allow_packaging_adjustment ? 'warning.200' : 'info.200',
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                                {intl.get("Dispensation.PackagingAdjustment").d("Commercial Adequacy")}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {quantityValidation.selectedTotalMg > quantityValidation.prescribedTotalMg
                                                                    ? intl.get("Dispensation.PackagingAdjustment.SurplusMessage").d("More day(s) than prescribed will be dispensed, as the available commercial presentation does not exactly match the prescribed quantity.")
                                                                        .replace("{{days}}", quantityValidation.daysDifference || Math.abs(quantityValidation.selectedQuantity - quantityValidation.prescribedQuantity))
                                                                    : intl.get("Dispensation.PackagingAdjustment.DeficitMessage").d("Fewer day(s) than prescribed will be dispensed, as the available commercial presentation does not exactly match the prescribed quantity.")
                                                                        .replace("{{days}}", quantityValidation.daysDifference || Math.abs(quantityValidation.selectedQuantity - quantityValidation.prescribedQuantity))
                                                                }
                                                            </Typography>
                                                        </Paper>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        </Box>
                                    </Box>

                                    <Divider />

                                    {/* Regulatory Confirmations */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                            {intl.get("regulatory_confirmations").d("Regulatory Confirmations")} *
                                        </Typography>

                                        <Stack spacing={1}>
                                            <DmsCheckBox
                                                label={intl.get("ppg_counseling_confirmed").d("I confirm that I have counseled the patient about risks according to the PPG")}
                                                checked={formState.ppg_counseling_confirmed}
                                                onChange={handleCheckboxChange('ppg_counseling_confirmed')}
                                            />

                                            <DmsCheckBox
                                                label={intl.get("liability_term_received").d("I declare that I have received the signed liability term")}
                                                checked={formState.liability_term_received}
                                                onChange={handleCheckboxChange('liability_term_received')}
                                            />

                                            {quantityValidation.hasAlternativeStrengths && (
                                                <DmsCheckBox
                                                    label={intl.get("PharmacistAlternativeDispensationDeclaration").d("I declare that I performed the dispensation according to the prescribed dose and posology and delivered to the patient the active posology rectification addendum.")}
                                                    checked={formState.dosage_rectification_term_accepted}
                                                    onChange={handleCheckboxChange('dosage_rectification_term_accepted')}
                                                />
                                            )}

                                            {quantityValidation.needsPackagingAdjustment && (
                                                <DmsCheckBox
                                                    label={intl.get("Dispensation.PackagingAdjustment.Checkbox").d("I acknowledge the difference between prescribed and dispensed quantity due to commercial presentation")}
                                                    checked={formState.allow_packaging_adjustment}
                                                    onChange={handleCheckboxChange('allow_packaging_adjustment')}
                                                />
                                            )}
                                        </Stack>

                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            {intl.get("archive_documents_reminder").d(
                                                "Archive printed documents as required by RDC 735/22."
                                            )}
                                        </Typography>
                                    </Box>

                                            {/* Summary when valid */}
                                            {isFormValid && (
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        backgroundColor: 'success.50',
                                                        borderColor: 'success.200',
                                                    }}
                                                >
                                                    {/* Title */}
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                        <CheckCircleIcon fontSize="small" />
                                                        {intl.get("ready_to_dispense").d("Ready to dispense")}
                                                    </Typography>

                                                    {/* Prescribed */}
                                                    <Box sx={{ mb: 1 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                                            {intl.get("Prescribed").d("Prescribed")}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {selectedPrescription.dosage_description}
                                                        </Typography>
                                                    </Box>

                                                    {/* Being Dispensed */}
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                                            {intl.get("Being_Dispensed").d("Being Dispensed")}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {calculateTotalBoxes()} {intl.get("boxes").d("box(es)")} = {calculateTotalCapsules()} {intl.get("capsules").d("capsules")} ({calculateTotalMg()}mg)
                                                        </Typography>
                                                        {batchSelections.map((selection, index) => {
                                                            if (!selection.batch_details) return null;
                                                            const capCount = Number(selection.capsule_count) || 0;
                                                            const batchCapsules = selection.box_count * capCount;
                                                            const strength = extractStrengthFromName(selection.batch_details.part_name);
                                                            return (
                                                                <Typography key={index} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                    • {intl.get("Batch").d("Batch")} {selection.batch_details.number}: {selection.box_count} {intl.get("boxes").d("box(es)")} × {capCount} {intl.get("capsules").d("capsules")} = {batchCapsules} {intl.get("capsules").d("capsules")} × {strength}mg = {batchCapsules * strength}mg
                                                                </Typography>
                                                            );
                                                        })}
                                                        {adjustedCycleText && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    mt: 1.5,
                                                                    p: 1,
                                                                    backgroundColor: 'warning.50',
                                                                    borderRadius: 1,
                                                                    fontWeight: 500,
                                                                    color: 'warning.dark',
                                                                }}
                                                            >
                                                                {adjustedCycleText}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Paper>
                                            )}

                                            {/* Action Buttons */}
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                <DmsButton
                                                    variant="primary"
                                                    text={intl.get('Dispensation.Submit').d('Complete Dispensing')}
                                                    onClick={handleConfirmClick}
                                                    disabled={isSubmitting || !isFormValid}
                                                />

                                                <DmsButton
                                                    variant="warning"
                                                    text={intl.get('Dispensation.DoNotDispense').d('Do Not Dispense')}
                                                    onClick={() => setShowJustificationModal(true)}
                                                    disabled={isSubmitting}
                                                />
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            )}
                        </DmsAltBox.Body>
                    </DmsAltBox.Card>
                </Grid>
            </Grid>

            {/* Justification Modal */}
            <DmsModal
                isVisible={showJustificationModal}
                onClose={() => setShowJustificationModal(false)}
            >
                <DmsModal.Header
                    title={intl.get('Dispensation.JustificationTitle').d('Provide Justification')}
                    onClose={() => setShowJustificationModal(false)}
                />
                <DmsModal.Body>
                    <Box sx={{ px: 2, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <DmsAlertWarning
                            message={intl.get('Dispensation.DoNotDispenseWarning').d(
                                'By proceeding with this action the patient will have to return to the doctor to obtain a new prescription in order to continue treatment. Are you sure you want to continue?'
                            )}
                        />
                        <DmsTextField
                            label={`${intl.get('Dispensation.JustificationLabel').d('Justification for not dispensing')} *`}
                            value={justification}
                            onChange={(e) => setJustification(e.target.value)}
                            multiline
                            rows={4}
                            fullWidth
                            required
                        />
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        variant="secondary"
                        text={intl.get('Cancel').d('Cancel')}
                        onClick={() => setShowJustificationModal(false)}
                    />
                    <DmsButton
                        variant="primary"
                        text={intl.get('Submit').d('Submit')}
                        onClick={handleJustificationSubmit}
                        disabled={isSubmitting || !justification.trim()}
                    />
                </DmsModal.Footer>
            </DmsModal>

            {/* Electronic Signature Confirmation */}
            <DmsElectronicSignature
                show={showDoubleBlindModal}
                setShow={setShowDoubleBlindModal}
                onSuccess={handleSubmit}
                onClose={() => setShowDoubleBlindModal(false)}
                title={intl.get('Dispensation.ConfirmDispensing').d('Confirm Dispensing')}
                actionDescription={signatureActionDescription}
                logModule="DISPENSATION_SIGNATURE"
                data={{
                    action: "DISPENSE_MEDICATION",
                    prescription_pk: selectedPrescription?.prescription_pk,
                    prescription_identifier: selectedPrescription?.prescription_identifier,
                    patient_name: selectedPrescription?.patient_name,
                    patient_identifier: selectedPrescription?.patient_identifier,
                    batches: batchSelections.map(s => ({
                        batch_pk: s.batch_pk,
                        box_count: s.box_count,
                        batch_number: s.batch_details?.number
                    })),
                    total_boxes: calculateTotalBoxes(),
                    total_capsules: calculateTotalCapsules(),
                    total_mg: calculateTotalMg(),
                    dispensed_dose_strength: selectedPrescription?.medicine_dose_strength,
                    dispensed_dose_strength_unit: selectedPrescription?.medicine_dose_strength_unit,
                    ppg_counseling_confirmed: formState.ppg_counseling_confirmed,
                    liability_term_received: formState.liability_term_received,
                }}
            />

            {/* Training Warning Modal */}
            <DmsModal
                isVisible={showTrainingWarningModal}
                onClose={() => setShowTrainingWarningModal(false)}
            >
                <DmsModal.Header
                    title={trainingWarningTitle || intl.get("training_required").d("Training Required")}
                    onClose={() => setShowTrainingWarningModal(false)}
                />
                <DmsModal.Body>
                    <Box sx={{ p: 2 }}>
                        <DmsAlertWarning message={trainingWarningMessage} />
                        <Typography sx={{ mt: 2 }}>
                            {intl.get("training_access_instruction").d(
                                "Please navigate to the Training Materials section to complete the required training."
                            )}
                        </Typography>
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        variant="primary"
                        text={intl.get("ok").d("OK")}
                        onClick={() => setShowTrainingWarningModal(false)}
                    />
                </DmsModal.Footer>
            </DmsModal>

            {/* Dosage Rectification Term Dialog */}
            <LegalDocumentDialog
                open={termDialogOpen}
                onClose={() => setTermDialogOpen(false)}
                document={termDocument}
                loading={loadingTerm}
            />
        </DmsPage>
    );
};

export default DispensationPage;
